import React, { useEffect, useState } from 'react';
import { SuppliersManagementViewDrawerInfoRow } from './_suppliers-management-view-drawer-info-row';
import { Button, Form, Row, Select } from 'antd';
import { ErrorHandler, LoadingState } from '@common/components';
import { useQuery } from '@common/hooks';
import { FormInstance } from 'antd/es/form/Form';
import {
  SupplierTierQuery,
  TiersFlatType,
  TiersFlatQuery,
} from '@institutions/graphql';
import { tierHumanizeVisibilityOffset } from '@institutions/services';

type Props = {
  form: FormInstance;
  supplierId?: string;
  onFormSubmit: (value: Record<string, string>) => void;
  tierEditionState: [boolean, (value: boolean) => void];
};

export function SuppliersManagementViewDrawerTierSection({
  form,
  supplierId,
  onFormSubmit,
  tierEditionState,
}: Props) {
  const [editionMode, setEditionMode] = tierEditionState;
  const [selectedTierId, setSelectedTierId] = useState('');

  const availableTiers = useQuery(TiersFlatQuery);
  const supplierTier = useQuery(SupplierTierQuery, {
    variables: {
      id: supplierId || '',
    },
    skip: !supplierId,
  });

  useEffect(() => {
    const currentSupplierTierId =
      supplierTier.data?.supplier.tierForCurrentOrganization?.id || '';

    if (!editionMode) {
      setSelectedTierId(currentSupplierTierId);
      form.setFieldValue('selectedTierId', currentSupplierTierId);
      return;
    }

    if (!selectedTierId) {
      setSelectedTierId(currentSupplierTierId);
    }
  }, [editionMode, supplierTier.data, selectedTierId, form]);

  const error = availableTiers.error || supplierTier.error;
  const loading = availableTiers.loading || supplierTier.loading;
  const anyData = availableTiers.data || supplierTier.data;

  if (error) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="SuppliersManagementViewDrawerTierSection"
      />
    );
  }

  if (loading || !anyData) {
    return <LoadingState />;
  }

  const currentSupplierTier =
    supplierTier.data?.supplier.tierForCurrentOrganization;

  if (!currentSupplierTier) {
    return null;
  }

  const availableTiersList = availableTiers.data?.tiers || [];

  const tierOptions =
    availableTiersList.map((tier) => ({
      label: tier.name,
      value: tier.id,
    })) || [];

  const previewTier = editionMode
    ? availableTiersList.find(
        (tier: TiersFlatType) => tier.id === selectedTierId
      )
    : currentSupplierTier;

  const requestDelayHumanized = tierHumanizeVisibilityOffset(
    previewTier?.visibilityOffset
  );

  return (
    <>
      <div className="supplier-management-view-drawer-tier-section">
        <div className="tier-form">
          {editionMode ? (
            <Form
              form={form}
              onFinish={onFormSubmit}
              initialValues={{ selectedTierId: currentSupplierTier?.id }}
            >
              <Form.Item
                name="selectedTierId"
                rules={[{ required: true, message: 'Please select a tier' }]}
              >
                <Select
                  style={{ width: 240 }}
                  placeholder="Select the tier"
                  options={tierOptions}
                  onChange={setSelectedTierId}
                />
              </Form.Item>
            </Form>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <span style={{ fontSize: 18 }}>{currentSupplierTier?.name}</span>
              <Button type="link" onClick={() => setEditionMode(true)}>
                Change
              </Button>
            </div>
          )}
        </div>
        <Row gutter={[0, 12]}>
          <SuppliersManagementViewDrawerInfoRow
            label="Staffing Request Delay"
            info={requestDelayHumanized}
            firstColumnOffset={7}
          />
          <SuppliersManagementViewDrawerInfoRow
            label="Admin Fee"
            info={`${previewTier?.fee}%`}
            firstColumnOffset={7}
          />
        </Row>
      </div>
      <style jsx>{`
        .supplier-management-view-drawer-tier-section .tier-form {
          display: inline-block;
        }
      `}</style>
    </>
  );
}
